import React, { FormEvent, useRef, useState } from "react";
import styled from "styled-components/macro";
import Dialog from "@fluentui/react/lib/Dialog";
import Button from "../../shared/Button";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/rootState";
import { ResourceStrings } from "../../../ResourceStrings";
import Flex from "../../shared/Flex";
import { Dropdown, IDropdownOption } from "@fluentui/react/lib/Dropdown";
import { ChildPost, PostId, PostType, RootPost } from "../../../RootPost";
import { NodeType } from "./RichNodeEditor";

export interface ConvertSolutionOrWorarkaroundDialogProps {
    solution: ChildPost;
    ticket: RootPost;
    isOpen: boolean;
    convertFrom: NodeType;
    onSubmit: (convertTo: NodeType, parentNodeId?: number) => void;
    onDismiss?: () => void;
}

export default function ConvertSolutionOrWorkaroundDialog(props: ConvertSolutionOrWorarkaroundDialogProps): JSX.Element {
    const [error, setError] = useState<string>(null);
    const [isLegal, setIsLegal] = useState<boolean>(false);
    const [parentNodeId, setParentNodeId] = useState(props.ticket.id);
    const refConvertBtn = useRef(null);
    const [selectedOption, setSelectedOption] = useState<NodeType>();
    const solutionsOrWorkaroundsForTicket = useSelector((state: RootState) => (state.appState.ticketsById[props.ticket.id]?.data?.children ?? []).filter(child => (child.type === PostType.solution || child.type === PostType.workaround)));

    const otherSolutions = solutionsOrWorkaroundsForTicket.filter(x => x.id !== props.solution.id);
    const convertToCommentUnderList: IDropdownOption[] = [
        {
            key: props.ticket.id,
            text: `under the ${props.ticket.type === PostType.suggestion ? "suggestion" : "problem"} by ${props.ticket.author.name} on ${props.ticket.createdDate}`
        },
        ...otherSolutions.map(x => ({
            key: x.id,
            text: `under the solution by ${x.author.name} on ${x.createdDate}`
        }))
    ];

    const onDismiss = (): void => {
        setError(null);
        props.onDismiss();
    }

    return <DialogWrapper
        hidden={!props.isOpen}
        onDismiss={onDismiss}
        modalProps={{
            layerProps: {
                onLayerDidMount: () => {
                    setTimeout(() => {
                        refConvertBtn.current?.focus();
                    }, 100);
                }
            },
            isBlocking: false,
            styles: {
                main: {
                    border: `solid 2px gray`,
                    borderRadius: '.8rem',
                }
            }
        }}
        dialogContentProps={{
            showCloseButton: true,
            title: "Convert solution",
            closeButtonAriaLabel: ResourceStrings.close(),
        }}
    >
        <div
            id="notificationMessageReader"
            className="visually-hidden"
            role={"status"}
            aria-live="assertive"
        >
            Convert solution
        </div>

        <Flex column densityVer="relaxed">
            <Dropdown
                options={(props.convertFrom === NodeType.solution) ? [{ key: NodeType.comment, text: "to a comment" }, { key: NodeType.workaround, text: "to a workaround" }]
                    : [{ key: NodeType.comment, text: "to a comment" }, { key: NodeType.solution, text: "to a solution" }]}
                placeholder="Convert to: comment or workaround"
                aria-label="Select conversion type"
                onChange={(event: FormEvent<HTMLDivElement>, item: IDropdownOption) => setSelectedOption(item.key as NodeType)}
            />

            {(selectedOption === NodeType.comment) && <Dropdown
                options={convertToCommentUnderList}
                placeholder="Please select the parent of this item"
                aria-label="Select parent post for converted item"
                onChange={(event: FormEvent<HTMLDivElement>, item: IDropdownOption) => { setParentNodeId(item.key as PostId); setIsLegal(true); }}
            />
            }

            {error && <div className="error">{error}</div>}
            <Flex justifyContent="flex-end" densityHor="normal" className="buttons">
                <Button onClick={onDismiss} buttonType="normal">Close</Button>
                {/* isLegal tackles the case where user tries to convert something to a comment without choosing a parent */}
                <Button ref={refConvertBtn} onClick={() => {
                    (isLegal || selectedOption !== NodeType.comment) && ((selectedOption === NodeType.comment) ? props.onSubmit(selectedOption as NodeType, parentNodeId)
                    : props.onSubmit(selectedOption as NodeType, props.ticket.id))}}>Ok</Button>
            </Flex>
        </Flex>
    </DialogWrapper>
}

const DialogWrapper = styled(Dialog)`
    .ms-Dialog-main {
        min-width: 50rem;
        max-width: 90rem;
    }

    .ms-Dialog-inner {
        padding-top: 2rem;
    }

    .ms-Dropdown-container {
        margin-bottom: 2rem;
    }

    .buttons {
        margin-top: 2rem;
    }

    .url {
        border: solid 1px var(--color-on-surface-lighter);
        padding: .5rem 1rem;
        margin-bottom: 3rem;
    }
`;
